import type {
  DeleteStripeBillingPaymentMethod,
  GetCardPaymentFailures,
  GetClientBillingBalance,
  GetClientBillingInvoicePdf,
  GetClientBillingOverview,
  GetStripeBillingPaymentMethod,
  GetClientEracuniInvoices,
  PostStripeBillingPaymentMethod,
  PostStripeBillingRetryCharge,
  PostStripeBillingSetupIntent,
} from "@/server-types";
import type { AxiosError } from "axios";
import type { UseMutationOptions } from "@tanstack/react-query";
import { del, get, post } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";
import { arrayBufferToBase64 } from "@/utils/saveFile";

type MutationOpts<TData, TVariables> = Omit<
  UseMutationOptions<TData, AxiosError, TVariables>,
  "mutationFn" | "onError"
> & {
  displayNotification?: boolean;
  onError?: (
    error: AxiosError,
    variables: TVariables,
    context: unknown,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
};

const PAYMENT_METHOD_KEY = ["clientBillingPaymentMethod"];
const CARD_FAILURES_KEY = ["clientCardPaymentFailures"];

function invalidatePaymentMethod() {
  queryClient.invalidateQueries({ queryKey: PAYMENT_METHOD_KEY });
  invalidateCardFailures();
}

// Saving a card charges the open failures, so the banner and the invoice list
// both change with it.
function invalidateCardFailures() {
  queryClient.invalidateQueries({ queryKey: CARD_FAILURES_KEY });
  queryClient.invalidateQueries({ queryKey: ["clientBilling"] });
}

/**
 * GET /client/billing — tier summary + the client's own (Lime) invoices.
 * Fast: reads stored columns only, no eracuni round-trip.
 */
export const useGetClientBilling = (options?: {
  displayNotification?: boolean;
  /** The endpoint is admin-only, so callers rendered for every user must pass
   * false — otherwise every page visit fires a 403. */
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["clientBilling"],
    queryFn: () => get<GetClientBillingOverview>("/client/billing"),
    displayNotification: options?.displayNotification,
    enabled: options?.enabled ?? true,
  });
};

/**
 * GET /client/billing/eracuni-invoices — invoices issued directly in eracuni.
 * Slower (hits eracuni); loaded in the background and merged + deduped against
 * the Lime invoices on the client.
 */
export const useGetClientEracuniInvoices = (options?: {
  displayNotification?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["clientEracuniInvoices"],
    queryFn: () =>
      get<GetClientEracuniInvoices>("/client/billing/eracuni-invoices"),
    displayNotification: options?.displayNotification,
  });
};

/**
 * GET /client/billing/balance — open ledger balance (saldakonti) from eracuni.
 * Loaded in the background; the page falls back to invoice-derived totals when
 * `balance` is null.
 */
export const useGetClientBillingBalance = (options?: {
  displayNotification?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["clientBillingBalance"],
    queryFn: () => get<GetClientBillingBalance>("/client/billing/balance"),
    displayNotification: options?.displayNotification,
  });
};

/**
 * GET /client/billing/invoice/:documentId/pdf as base64, authenticated by the
 * session header. The mobile app needs this: navigating to the PDF URL there
 * opens the system browser, which has no navigation-ticket cookie.
 */
export const useFetchClientInvoicePdf = (
  options?: MutationOpts<string, { documentId: string }>,
) => {
  return useCustomMutation({
    mutationFn: async ({ documentId }: { documentId: string }) => {
      const pdf = await get<GetClientBillingInvoicePdf>(
        `/client/billing/invoice/${encodeURIComponent(documentId)}/pdf`,
        undefined,
        { responseType: "arraybuffer" },
      );
      return arrayBufferToBase64(pdf);
    },
    ...options,
  });
};

/**
 * GET /stripe/billing/payment-method — the card on file for Lime's own
 * subscription billing. Reads stored columns only; `isConfigured` is false when
 * the Lime billing Stripe account has no key in this environment.
 */
export const useGetStripeBillingPaymentMethod = (options?: {
  displayNotification?: boolean;
  /** The endpoint is gated on the `limeBillingCard` feature, so callers without
   * it must pass false — otherwise every Billing page visit fires a 403. */
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: PAYMENT_METHOD_KEY,
    queryFn: () =>
      get<GetStripeBillingPaymentMethod>("/stripe/billing/payment-method"),
    displayNotification: options?.displayNotification,
    enabled: options?.enabled ?? true,
  });
};

/**
 * POST /stripe/billing/setup-intent — opens a Stripe SetupIntent
 * whose client secret the card form confirms against.
 */
export const useCreateBillingSetupIntent = (
  options?: MutationOpts<PostStripeBillingSetupIntent["response"], void>,
) => {
  return useCustomMutation({
    mutationFn: () =>
      post<PostStripeBillingSetupIntent>("/stripe/billing/setup-intent", {}),
    ...options,
  });
};

/**
 * POST /stripe/billing/payment-method — attaches the confirmed card. The
 * `setup_intent.succeeded` webhook does the same thing, so a 3-D Secure redirect
 * that never returns here still saves the card.
 */
export const useSaveBillingPaymentMethod = (
  options?: MutationOpts<
    PostStripeBillingPaymentMethod["response"],
    PostStripeBillingPaymentMethod["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PostStripeBillingPaymentMethod["body"]) =>
      post<PostStripeBillingPaymentMethod>(
        "/stripe/billing/payment-method",
        body,
      ),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidatePaymentMethod();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

/** DELETE /stripe/billing/payment-method — detaches the card in Stripe. */
export const useDeleteBillingPaymentMethod = (
  options?: MutationOpts<DeleteStripeBillingPaymentMethod["response"], void>,
) => {
  return useCustomMutation({
    mutationFn: () =>
      del<DeleteStripeBillingPaymentMethod>("/stripe/billing/payment-method"),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidatePaymentMethod();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

/**
 * GET /client/billing/card-failures — declined card payments still being
 * collected. Open to every tenant user, since the banner shows for all of them.
 */
export const useGetCardPaymentFailures = (options?: {
  /** Off for clients without `limeBillingCard`, who can have none. */
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: CARD_FAILURES_KEY,
    queryFn: () => get<GetCardPaymentFailures>("/client/billing/card-failures"),
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
};

/** POST /stripe/billing/retry-charge — charges the saved card again now. */
export const useRetryCardCharge = (
  options?: MutationOpts<PostStripeBillingRetryCharge["response"], void>,
) => {
  return useCustomMutation({
    mutationFn: () =>
      post<PostStripeBillingRetryCharge>("/stripe/billing/retry-charge", {}),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      invalidateCardFailures();
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
